import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import './OneYearWrapped.css';

// ── Calendar config ────────────────────────────────────────────────────────────

const MONTHS_CONFIG = [
  { year: 2025, month: 8,  totalDays: 31, greyedDays: [1, 2] },
  { year: 2025, month: 9,  totalDays: 30 },
  { year: 2025, month: 10, totalDays: 31 },
  { year: 2025, month: 11, totalDays: 30 },
  { year: 2025, month: 12, totalDays: 31 },
  { year: 2026, month: 1,  totalDays: 31 },
  { year: 2026, month: 2,  totalDays: 28 },
  { year: 2026, month: 3,  totalDays: 31 },
  { year: 2026, month: 4,  totalDays: 30 },
  { year: 2026, month: 5,  totalDays: 31 },
  { year: 2026, month: 6,  totalDays: 30 },
  { year: 2026, month: 7,  totalDays: 31 },
  { year: 2026, month: 8,  totalDays: 2 },
];

const MONTH_SHORT = ['', 'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const DOW_INITIAL = ['S', 'M', 'T', 'W', 'T', 'F', 'S']; // Sun–Sat

const DAY_MS = 86400000;
const MERGE_GAP_MS = 20 * 60 * 1000; // 20-minute merge window
const MIN_FRAC = 2 / 100;            // minimum bar width as fraction of day
const LABEL_H = 13;                  // day-number label height in px (must match CSS)

const LANE_CONFIG = [
  { key: 'call',     label: 'Call',     color: '#22c55e', test: c => !c.isMissedOrCanceled && !c.isFaceTime },
  { key: 'facetime', label: 'FaceTime', color: '#3b82f6', test: c => !c.isMissedOrCanceled &&  c.isFaceTime },
];

const SLIDES = ['title', 'calendar'];

// ── CSV parsing ────────────────────────────────────────────────────────────────

function parseCallDate(s) {
  if (!s) return null;
  const parts = s.trim().split(' ');
  if (parts.length < 3) return null;
  const [datePart, timePart, ampm] = parts;
  const [m, d, y2] = datePart.split('/').map(Number);
  const year = y2 < 50 ? 2000 + y2 : 1900 + y2;
  let [hours, minutes] = timePart.split(':').map(Number);
  if (ampm === 'PM' && hours !== 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;
  return new Date(year, m - 1, d, hours, minutes, 0);
}

function parseDurationSec(s) {
  if (!s || s === 'Missed' || s === 'Canceled' || s === 'Incoming Call') return null;
  const parts = s.split(':').map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

function formatCallDate(date) {
  const mo = date.getMonth() + 1;
  const d = date.getDate();
  const y = String(date.getFullYear()).slice(-2);
  let h = date.getHours();
  const min = String(date.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${mo}/${d}/${y} ${h}:${min} ${ampm}`;
}

function formatDuration(sec) {
  if (!sec || sec < 60) return `${sec || 0}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}

function parseCalls(rows) {
  return rows.flatMap(row => {
    const type = (row['Type of Call'] || '').trim();
    const dateStr = (row['Date'] || '').trim();
    const durStr = (row['Duration'] || '').trim();
    const format = (row['Format'] || '').trim();
    const note = (row['Notes'] || '').trim();
    if (!dateStr || !type) return [];
    const startDate = parseCallDate(dateStr);
    if (!startDate) return [];
    const durationSec = parseDurationSec(durStr);
    const isMissedOrCanceled = !durationSec || durationSec <= 10;
    return [{
      startDate,
      durationSec: durationSec || 0,
      isMissedOrCanceled,
      isOutgoing: type === 'Outgoing Call',
      isFaceTime: format === 'FaceTime',
      note,
    }];
  });
}

// ── Pre-compute calendar data ─────────────────────────────────────────────────

function buildCalendarData(calls) {
  // Sort once, then merge each lane's calls globally (≤20 min gap → single bar)
  const sortedCalls = [...calls].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  // Build day → note map (first note wins per day)
  const dayNoteMap = new Map();
  calls.forEach(c => {
    if (!c.note) return;
    const dayKey = new Date(c.startDate.getFullYear(), c.startDate.getMonth(), c.startDate.getDate()).getTime();
    if (!dayNoteMap.has(dayKey)) dayNoteMap.set(dayKey, c.note);
  });

  const mergedPerLane = LANE_CONFIG.map(({ test }) => {
    const intervals = sortedCalls.filter(test).map(c => ({
      start: c.startDate.getTime(),
      end: c.startDate.getTime() + Math.max(c.durationSec * 1000, 1000),
      sourceCalls: [c],
    }));
    const merged = [];
    for (const iv of intervals) {
      if (!merged.length) { merged.push({ ...iv }); continue; }
      const last = merged[merged.length - 1];
      if (iv.start - last.end <= MERGE_GAP_MS) {
        last.end = Math.max(last.end, iv.end);
        last.sourceCalls = last.sourceCalls.concat(iv.sourceCalls);
      } else {
        merged.push({ ...iv });
      }
    }
    return merged;
  });

  return MONTHS_CONFIG.map(cfg => {
    const greyedDays = cfg.greyedDays || [];
    const monthStart = new Date(cfg.year, cfg.month - 1, 1, 0, 0, 0).getTime();
    const monthEnd = monthStart + cfg.totalDays * DAY_MS;

    // Row-level segments: each may span across multiple day cells within this month.
    // dayIdx / endDayIdx are 0-based indices into this month's days array.
    const segments = [];
    mergedPerLane.forEach((laneIntervals, laneIdx) => {
      const laneLabel = LANE_CONFIG[laneIdx].label;
      for (const { start, end, sourceCalls } of laneIntervals) {
        if (start >= monthEnd || end <= monthStart) continue;
        const cs = Math.max(start, monthStart);
        const ce = Math.min(end, monthEnd);

        const startOffMs = cs - monthStart;
        const endOffMs   = ce - monthStart;

        const dayIdx   = Math.floor(startOffMs / DAY_MS);
        const startFrac = (startOffMs - dayIdx * DAY_MS) / DAY_MS;

        let endDayIdx = Math.floor(endOffMs / DAY_MS);
        let endFrac   = (endOffMs - endDayIdx * DAY_MS) / DAY_MS;

        // end exactly at midnight → treat as end of the previous day
        if (endFrac === 0 && endDayIdx > dayIdx) { endDayIdx -= 1; endFrac = 1; }
        endDayIdx = Math.min(endDayIdx, cfg.totalDays - 1);

        const tooltip = sourceCalls
          .map(c => `${laneLabel}: ${formatCallDate(c.startDate)}, ${formatDuration(c.durationSec)}`)
          .join('\n');

        segments.push({ dayIdx, startFrac, endDayIdx, endFrac, lane: laneIdx, color: LANE_CONFIG[laneIdx].color, tooltip });
      }
    });

    const days = Array.from({ length: cfg.totalDays }, (_, i) => {
      const dayKey = new Date(cfg.year, cfg.month - 1, i + 1).getTime();
      const note = dayNoteMap.get(dayKey) || '';
      let noteClass = '';
      if (note.includes('Kayla')) noteClass = 'note-kayla';
      else if (note.includes('Eleina')) noteClass = 'note-eleina';
      else if (note.includes('Bruce')) noteClass = 'note-bruce';
      return {
        day: i + 1,
        greyed: greyedDays.includes(i + 1),
        dow: new Date(cfg.year, cfg.month - 1, i + 1).getDay(),
        noteClass,
      };
    });

    return { ...cfg, days, segments };
  });
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ProgressCapsules({ slide, onJump }) {
  return (
    <div className="oyw-capsules" onClick={e => e.stopPropagation()}>
      {SLIDES.map((s, i) => (
        <button
          key={s}
          className={`oyw-capsule ${i === slide ? 'active' : i < slide ? 'done' : ''}`}
          onClick={e => { e.stopPropagation(); onJump(i); }}
          aria-label={`Go to slide ${i + 1}`}
        />
      ))}
    </div>
  );
}

function NavArrows({ slide, total, onPrev, onNext }) {
  return (
    <>
      {slide > 0 && (
        <button className="oyw-arrow oyw-arrow-left" onClick={e => { e.stopPropagation(); onPrev(); }} aria-label="Previous">
          ‹
        </button>
      )}
      {slide < total - 1 && (
        <button className="oyw-arrow oyw-arrow-right" onClick={e => { e.stopPropagation(); onNext(); }} aria-label="Next">
          ›
        </button>
      )}
    </>
  );
}

function TitleSlide() {
  return (
    <div className="oyw-slide-inner oyw-title-bg">
      <div className="oyw-title-content">
        <p className="oyw-names">Reina &amp; Reilly</p>
        <h1 className="oyw-title-main">One Year<br />Wrapped</h1>
        <div className="oyw-teal-line" />
        <div className="oyw-date-pills">
          <span className="oyw-pill">03</span>
          <span className="oyw-pill">August</span>
          <span className="oyw-pill">2026</span>
        </div>
      </div>
      <p className="oyw-hint">tap anywhere to continue</p>
    </div>
  );
}

function StatBlock({ value, label, sub, accent }) {
  return (
    <div className="oyw-stat-block">
      <span className="oyw-stat-value" style={accent ? { color: accent } : {}}>{value}</span>
      <span className="oyw-stat-label">{label}</span>
      {sub && <span className="oyw-stat-sub">{sub}</span>}
    </div>
  );
}

function ReviewSlide() {
  return (
    <div className="oyw-slide-inner oyw-dark-bg">
      <div className="oyw-section-header">
        <h2 className="oyw-section-title">Performance Review <span className="oyw-accent-mark">≤—</span></h2>
        <p className="oyw-section-sub">Reina "cutiebear" Kim</p>
      </div>
      <div className="oyw-stats-grid">
        <StatBlock value="10/10" label="Customer Satisfaction Score" sub="would do again" accent="#4dd0e1" />
        <StatBlock value="100%" label="Year 1 Retention" sub="hot commodity" />
        <StatBlock value="0%" label="Product Defect Rate" sub="literally flawless" accent="#4dd0e1" />
        <StatBlock value="∞" label="Customer Lifetime Value" sub="i'd like to renew my subscription" />
        <StatBlock value="38" label={'"it\'s fine i\'m fine" counter'} sub="very p'etty" />
        <StatBlock value="Hard" label="Difficulty Saying Goodbye To" sub=":(" accent="#4dd0e1" />
        <StatBlock value="0" label="Major Incidents" sub="it's like we get along" />
        <StatBlock value="849" label="Judgments Counter" sub="beware the reina stare" accent="#4dd0e1" />
      </div>
    </div>
  );
}

function NumbersSlide() {
  return (
    <div className="oyw-slide-inner oyw-dark-bg">
      <div className="oyw-section-header">
        <h2 className="oyw-section-title">Thanking My Lucky Stars <span className="oyw-accent-mark">≤—</span></h2>
        <p className="oyw-section-sub">after <strong>one year</strong> of you &amp; me…</p>
      </div>
      <div className="oyw-stats-grid">
        <StatBlock value="365" label="days together (officially)" accent="#4dd0e1" />
        <StatBlock value="211" label="miles between us" />
        <StatBlock value="25+" label="movie nights" accent="#4dd0e1" />
        <StatBlock value="31+" label="movies &amp; shows watched together" />
        <StatBlock value="9" label="years we've known each other" />
        <StatBlock value="10+" label="times we've hung out in person" accent="#4dd0e1" />
        <StatBlock value="305" label="calls between us (>10 sec)" />
        <StatBlock value="256h 40m" label="total call time" sub="longest: 6h 3min · Jan 14, 2026" accent="#4dd0e1" />
      </div>
    </div>
  );
}

function DayCell({ dayData, cellWidth, laneHeight }) {
  const { day, greyed, dow, noteClass } = dayData;
  const isWeekend = dow === 0 || dow === 6;
  const barAreaHeight = LANE_CONFIG.length * laneHeight;

  const cls = ['oyw-day-cell',
    greyed ? 'greyed' : '',
    isWeekend ? 'weekend' : '',
    noteClass || '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} style={{ width: cellWidth, minWidth: cellWidth }}>
      <span className="oyw-day-num">{day} ({DOW_INITIAL[dow]})</span>
      <div style={{ height: barAreaHeight }} />
    </div>
  );
}

function CalendarRow({ monthData, cellWidth, laneHeight }) {
  const { year, month, days, segments } = monthData;
  const barAreaHeight = LANE_CONFIG.length * laneHeight;
  // cellWidth + 1px gap between each cell; bar positions use this stride
  const stride = cellWidth + 1;

  return (
    <div className="oyw-cal-row">
      <div className="oyw-month-label">
        <span className="oyw-month-abbr">{MONTH_SHORT[month]}</span>
        <span className="oyw-month-year">{year}</span>
      </div>
      <div className="oyw-days-row-wrap">
        <div className="oyw-days-row">
          {days.map(d => (
            <DayCell key={d.day} dayData={d} cellWidth={cellWidth} laneHeight={laneHeight} />
          ))}
        </div>
        <div className="oyw-bars-overlay" style={{ top: LABEL_H, height: barAreaHeight }}>
          {segments.map((seg, i) => {
            const barLeft  = seg.dayIdx    * stride + seg.startFrac * cellWidth;
            const barRight = seg.endDayIdx * stride + seg.endFrac   * cellWidth;
            const barWidth = Math.max(barRight - barLeft, 2);
            return (
              <div
                key={i}
                className="oyw-bar"
                title={seg.tooltip}
                style={{
                  left: barLeft,
                  width: barWidth,
                  top: seg.lane * laneHeight,
                  height: laneHeight - 1,
                  backgroundColor: seg.color,
                  borderRadius: 1,
                  pointerEvents: 'auto',
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CalendarSlide({ calendarData, loading }) {
  const containerRef = useRef(null);
  const [cellWidth, setCellWidth] = useState(32);
  const LANE_H = 16;
  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const available = w - 68 - 30 - 124; // label(68) + cell-gaps(30) + scroll-padding(2×62)
      setCellWidth(Math.max(16, Math.floor(available / 31)));
    };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="oyw-slide-inner oyw-cal-slide" onClick={e => e.stopPropagation()}>
      <div className="oyw-cal-header">
        <h2 className="oyw-section-title">One Year of Calling You ❤️</h2>
        <div className="oyw-legend">
          <span className="oyw-legend-item"><span className="oyw-legend-dot" style={{ background: '#22c55e' }} /> Call</span>
          <span className="oyw-legend-item"><span className="oyw-legend-dot" style={{ background: '#3b82f6' }} /> FaceTime</span>
        </div>
        <p className="oyw-cal-note">Each of our calls over the past year is visualized here as a bar showing when it happened, how long it was, and what type of call.</p>
      </div>
      <div className="oyw-cal-scroll" ref={containerRef}>
        {loading ? (
          <div className="oyw-loading">Loading calls…</div>
        ) : (
          calendarData.map((m, i) => (
            <CalendarRow key={i} monthData={m} cellWidth={cellWidth} laneHeight={LANE_H} />
          ))
        )}
      </div>
      <div className="oyw-cal-footer">
        <div className="oyw-legend oyw-cal-footnotes">
          <span className="oyw-legend-item"><span className="oyw-legend-dot oyw-fnote-dot" style={{ background: '#4d2d73' }} /> with Eleina</span>
          <span className="oyw-legend-item"><span className="oyw-legend-dot oyw-fnote-dot" style={{ background: '#6b5517' }} /> with Kayla &amp; Eleina</span>
          <span className="oyw-legend-item"><span className="oyw-legend-dot oyw-fnote-dot" style={{ background: '#1e3f6b' }} /> with Bruce</span>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function OneYearWrapped() {
  const [slide, setSlide] = useState(0);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/1yr-call-history.csv')
      .then(r => r.text())
      .then(csv => {
        const { data } = Papa.parse(csv, { header: true, skipEmptyLines: true });
        setCalls(parseCalls(data));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const calendarData = useMemo(() => loading ? [] : buildCalendarData(calls), [calls, loading]);

  const advance = useCallback(() => setSlide(s => Math.min(SLIDES.length - 1, s + 1)), []);
  const retreat = useCallback(() => setSlide(s => Math.max(0, s - 1)), []);

  // Swipe support
  const touchStartX = useRef(null);
  const onTouchStart = useCallback(e => { touchStartX.current = e.touches[0].clientX; }, []);
  const onTouchEnd = useCallback(e => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -40) advance();
    else if (dx > 40) retreat();
    touchStartX.current = null;
  }, [advance, retreat]);

  // Keyboard support
  useEffect(() => {
    const handler = e => {
      if (e.key === 'ArrowRight') advance();
      if (e.key === 'ArrowLeft') retreat();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [advance, retreat]);

  const getSlideClass = i => {
    if (i === slide) return 'oyw-slide-wrap active';
    if (i < slide) return 'oyw-slide-wrap left';
    return 'oyw-slide-wrap right';
  };

  const isCalendar = SLIDES[slide] === 'calendar';

  return (
    <div
      className="oyw-root"
      onClick={isCalendar ? undefined : advance}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <ProgressCapsules slide={slide} onJump={setSlide} />
      <NavArrows slide={slide} total={SLIDES.length} onPrev={retreat} onNext={advance} />

      <div className={getSlideClass(0)}><TitleSlide /></div>
      <div className={getSlideClass(1)}>
        <CalendarSlide calendarData={calendarData} loading={loading} />
      </div>
    </div>
  );
}
